import { UserLevel } from '@/types';

const LEVEL_TITLES: Record<number, string> = {
  1:  'Newcomer',
  5:  'Apprentice',
  10: 'Collector',
  20: 'Enthusiast',
  30: 'Veteran',
  40: 'Expert',
  50: 'Master',
  75: 'Grandmaster',
  100: 'Legend',
};

function getLevelTitle(level: number): string {
  const thresholds = Object.keys(LEVEL_TITLES).map(Number).sort((a, b) => b - a);
  for (const t of thresholds) {
    if (level >= t) return LEVEL_TITLES[t];
  }
  return 'Newcomer';
}

interface Props {
  level: UserLevel;
  compact?: boolean;
  className?: string;
}

export default function LevelProgress({ level, compact = false, className = '' }: Props) {
  const title = getLevelTitle(level.level);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white text-xs font-bold flex-shrink-0">
          {level.level}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-medium text-gray-700 dark:text-zinc-300">{title}</span>
            <span className="text-xs text-gray-500 dark:text-zinc-500">{level.current_xp}/{level.xp_to_next_level} XP</span>
          </div>
          <div className="h-1.5 bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-500"
              style={{ width: `${level.progress_percentage}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5 ${className}`}>
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white text-2xl font-bold shadow-md flex-shrink-0">
          {level.level}
        </div>
        <div>
          <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-zinc-100">Level {level.level}</p>
          <p className="text-sm text-gray-500 dark:text-zinc-500">{level.total_xp.toLocaleString()} total XP</p>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-600 dark:text-zinc-400">Progress to Level {level.level + 1}</span>
          <span className="text-xs text-gray-500 dark:text-zinc-500">
            {level.current_xp.toLocaleString()} / {level.xp_to_next_level.toLocaleString()} XP
          </span>
        </div>
        <div className="h-3 bg-gray-100 dark:bg-zinc-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-700"
            style={{ width: `${level.progress_percentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1 text-right">{level.progress_percentage}% complete</p>
      </div>
    </div>
  );
}
